import React, { useRef } from 'react';
import {
  Typography,
  Box,
  Container,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { shades } from '../../../theme';
import mtreatHome from '../../data/home/mHome.jpg';
import BloodPressureMonitor from '../../data/home/BloodPressureMonitor.jpg';
import digitalThermometer from '../../data/home/digitalThermometer.jpg';
import ElectricWheelchair from '../../data/home/ElectricWheelchair.jpg';
import GlucoseMeter from '../../data/home/GlucoseMeter.jpg';
import NebulizerMachine from '../../data/home/NebulizerMachine.jpg';
import PulseOximeter from '../../data/home/PulseOximeter.jpg';
import WalkingCane from '../../data/home/WalkingCane.jpg';

const FeaturedEquipments = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery('(max-width:600px)');

  const medicalEquipments = [
    {
      id: 1,
      name: 'Digital Thermometer',
      originalPrice: 1500,
      currentPrice: 1125,
      discount: '-25%',
      rating: 4.5,
      reviews: 87,
      image: digitalThermometer
    },
    {
      id: 2,
      name: 'Blood Pressure Monitor',
      originalPrice: 8500,
      currentPrice: 6375,
      discount: '-25%',
      rating: 4.7,
      reviews: 65,
      image: BloodPressureMonitor
    },
    {
      id: 3,
      name: 'Pulse Oximeter',
      originalPrice: 3000,
      currentPrice: 2250,
      discount: '-25%',
      rating: 4.6,
      reviews: 92,
      image: PulseOximeter
    },
    {
      id: 4,
      name: 'Glucose Meter',
      originalPrice: 5000,
      currentPrice: 3750,
      discount: '-25%',
      rating: 4.8,
      reviews: 54,
      image: GlucoseMeter
    },
    {
      id: 5,
      name: 'Nebulizer Machine',
      originalPrice: 7000,
      currentPrice: 5250,
      discount: '-25%',
      rating: 4.4,
      reviews: 48,
      image: NebulizerMachine
    },
    {
      id: 6,
      name: 'Electric Wheelchair',
      originalPrice: 35000,
      currentPrice: 26250,
      discount: '-25%',
      rating: 4.9,
      reviews: 32,
      image: ElectricWheelchair
    },
    {
      id: 7,
      name: 'Walking Cane',
      originalPrice: 2000,
      currentPrice: 1500,
      discount: '-25%',
      rating: 4.3,
      reviews: 76,
      image: WalkingCane
    }
  ];

  const scrollRef = useRef(null);

  const handleScroll = (direction) => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const scrollAmount = direction === 'left' ? -300 : 300;

      // Get current scroll position
      const startPosition = container.scrollLeft;
      const targetPosition = startPosition + scrollAmount;

      container.scrollTo({
        left: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) =>
      index < Math.floor(rating) ? '★' : '☆'
    ).join('');
  };

  return (
    <Container maxWidth='xl' sx={{ py: 3, mt: 4 }}>
      <Box
        sx={{
				  display: 'flex',
				  alignItems: 'center',
				  mb: 2,
				  justifyContent: 'space-between'
        }}
      >
        <Box>
          <Typography
            variant={isMobile ? 'body2' : 'body1'}
            sx={{
						  fontWeight: 600,
						  color: isDarkMode ? shades.white[100] : shades.secondary[500],
						  backgroundColor: isDarkMode
						    ? shades.neutral[800]
						    : shades.neutral[200],
						  display: 'inline-block',
						  px: 1.5,
						  py: 0.5,
						  borderRadius: 1,
						  mb: 1,
						  fontSize: isMobile ? '0.7rem' : '0.75rem'
            }}
          >
            Medical Essentials
          </Typography>
          <Typography
            variant={isMobile ? 'h4' : 'h2'}
            sx={{
						  fontWeight: 600,
						  color: isDarkMode ? shades.white[100] : shades.secondary[500]
            }}
          >
            Featured Equipment
          </Typography>
        </Box>
        <Box>
          <IconButton onClick={() => handleScroll('left')}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={() => handleScroll('right')}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
      <Box
        ref={scrollRef}
        sx={{
				  display: 'flex',
				  gap: 2,
				  overflowX: 'auto',
				  scrollBehavior: 'smooth',
				  position: 'relative',
				  '&::-webkit-scrollbar': { display: 'none' },
				  scrollbarWidth: 'none',
				  msOverflowStyle: 'none',
				  pb: 1
        }}
      >
        {medicalEquipments.map((equipment) => (
          <Box
            key={equipment.id}
            sx={{
						  flex: '0 0 auto',
						  width: isMobile ? 160 : 250,
						  position: 'relative',
						  p: 2,
						  borderRadius: 2,
						  backgroundColor: isDarkMode
						    ? shades.secondary[600]
						    : shades.white[100],
						  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
            }}
          >
            <Box
              sx={{
							  position: 'absolute',
							  top: 10,
							  left: 10,
							  backgroundColor: shades.neutral[700],
							  color: shades.white[100],
							  px: 1,
							  py: 0.5,
							  borderRadius: 1,
							  fontSize: '0.75rem'
              }}
            >
              {equipment.discount}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <IconButton size='small'>
                <AddShoppingCartIcon fontSize='small' />
              </IconButton>
            </Box>
            <Box
              sx={{
							  display: 'flex',
							  justifyContent: 'center',
							  alignItems: 'center',
							  mb: 2,
							  height: '150px',
							  width: '100%',
							  backgroundColor: '#f5f5f5',
							  borderRadius: '8px',
							  overflow: 'hidden'
              }}
            >
              <img
                src={equipment.image}
                alt={equipment.name}
                style={{
								  width: '100%',
								  height: '100%',
								  objectFit: 'cover',
								  borderRadius: '8px'
                }}
              />
            </Box>
            <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
              {equipment.name}
            </Typography>
            <Box
              sx={{
							  display: { xs: 'block', md: 'flex' },
							  justifyContent: 'space-between',
							  alignItems: 'center'
              }}
            >
              <Box>
                <Typography
                  variant='body2'
                  sx={{
									  textDecoration: 'line-through',
									  color: isDarkMode
									    ? shades.neutral[400]
									    : shades.neutral[500],
									  fontSize: {
									    sm: 12,
									    md: 14,
									    lg: 16,
									    xl: 18
                    },
									  lineHeight: 1.2
                  }}
                >
                  Kes {equipment.originalPrice}
                </Typography>
                <Typography
                  variant='body1'
                  sx={{
									  fontWeight: 600,
									  color: isDarkMode ? shades.accent[300] : shades.accent[700],
									  fontSize: {
									    sm: 12,
									    md: 14,
									    lg: 16,
									    xl: 18
                    },
									  lineHeight: 1.2,
									  mt: 0.5
                  }}
                >
                  Kes {equipment.currentPrice}
                </Typography>
              </Box>
              <Typography
                variant='body1'
                sx={{
								  display: 'flex',
								  alignItems: 'center',
								  fontSize: 14,
								  lineHeight: 1.2,
								  marginTop: { xs: 1, md: 0 }
                }}
              >
                <Box
                  component='span'
                  sx={{ color: isDarkMode ? '#FFFF00' : '#FFDF00' }}
                >
                  {renderStars(equipment.rating)}
                </Box>
								&nbsp;({equipment.reviews})
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
      {/* <Box
                component="button"
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    margin: "0 auto",
                    marginTop: 4,
                    padding: "12px 24px",
                    fontSize: "clamp(14px, 2vw, 18px)",
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.mode === "dark" ? "#fff" : "#000",
                    border: "none",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease",
                    "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                    },
                }}
            >
                View all equipments
            </Box> */}
    </Container>
  );
};

export default FeaturedEquipments;
